import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { TrendingUp } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import Card from '@/components/ui/Card';
import Stat from '@/components/ui/Stat';
import Select from '@/components/ui/Select';
import Toolbar from '@/components/ui/Toolbar';
import Spinner from '@/components/ui/Spinner';
import EmptyState from '@/components/ui/EmptyState';
import { fmtDate, fmtNum } from '@/lib/enums';

export default function TrainerEvolution() {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState('');
  const [userId, setUserId] = useState('');
  const [measurements, setMeasurements] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api
      .get('/students')
      .then((res) => {
        const list = (res.data || res).students || res.data || [];
        setStudents(list);
        if (list[0]) {
          setStudentId(String(list[0].id));
          setUserId(String(list[0].userId || list[0].user?.id || list[0].id));
        }
      })
      .catch(() => setStudents([]));
  }, []);

  useEffect(() => {
    if (!userId) return;
    setMeasurements(null);
    setSummary(null);
    api.get('/evolution/measurements', { userId })
      .then((res) => setMeasurements((res.data || res).measurements || res.data || []))
      .catch(() => setMeasurements([]));
    api.get('/evolution/summary', { userId })
      .then((res) => setSummary(res.data || res))
      .catch(() => setSummary(null));
  }, [userId]);

  const onSelect = (e) => {
    const id = e.target.value;
    setStudentId(id);
    const s = students.find((x) => String(x.id) === id);
    setUserId(String(s?.userId || s?.user?.id || s?.id || ''));
  };

  // A API devolve as medidas em ordem cronológica (ASC) — plota direto.
  const chart = (measurements || []).map((m) => ({
    d: fmtDate(m.date || m.createdAt).slice(0, 5),
    peso: m.weightKg ?? m.weight,
    gordura: m.bodyFatPct ?? m.bodyFat,
    cintura: m.measures?.cintura,
  }));

  // % de gordura mais recente (o summary não traz; vem da última medição).
  const latestBodyFat = (measurements || []).findLast?.((m) => m.bodyFatPct != null)?.bodyFatPct ?? null;

  return (
    <>
      <PageHeader kicker="Movez · Personal" title={t('nav.evolution')} />

      <Toolbar>
        <Select
          label={t('evolution.student')}
          value={studentId}
          onChange={onSelect}
          placeholder={students.length ? t('evolution.selectStudent') : t('evolution.noStudent')}
          options={students.map((s) => ({ value: String(s.id), label: s.name }))}
          style={{ minWidth: 260 }}
        />
      </Toolbar>

      {!userId ? (
        <EmptyState icon={TrendingUp} title={t('evolution.selectStudentTitle')} description={t('evolution.selectStudentDesc')} />
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--space-3)', marginBottom: 'var(--space-6)' }}>
            <Stat value={summary?.latestWeight == null ? '—' : `${fmtNum(summary.latestWeight)} kg`} label={t('evolution.currentWeight')} tone="accent" />
            <Stat value={summary?.weightDelta == null ? '—' : `${summary.weightDelta > 0 ? '+' : ''}${fmtNum(summary.weightDelta)} kg`} label={t('evolution.weightChange')} tone="hi" />
            <Stat value={summary?.imc == null ? '—' : fmtNum(summary.imc)} label={t('evolution.imc')} tone="hi" />
            <Stat value={latestBodyFat == null ? '—' : `${fmtNum(latestBodyFat)}%`} label={t('evolution.bodyFat')} tone="recovery" />
            <Stat value={fmtNum(summary?.measurementCount ?? (measurements?.length))} label={t('evolution.records')} tone="hi" />
          </div>

          <Card>
            <div className="kicker" style={{ marginBottom: 14 }}>{t('evolution.measurementsEvolution')}</div>
            {measurements === null ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-12)' }}><Spinner /></div>
            ) : chart.length === 0 ? (
              <EmptyState title={t('evolution.noMeasurements')} description={t('evolution.noMeasurementsDesc')} />
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
                  <XAxis dataKey="d" stroke="var(--low)" fontSize={11} />
                  <YAxis stroke="var(--low)" fontSize={11} />
                  <Tooltip contentStyle={{ background: 'var(--surf2)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--hi)' }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="peso" stroke="var(--volt)" strokeWidth={2} name={t('evolution.weightLine')} />
                  <Line type="monotone" dataKey="gordura" stroke="var(--frost)" strokeWidth={2} name={t('evolution.bodyFatLine')} />
                  <Line type="monotone" dataKey="cintura" stroke="var(--warning)" strokeWidth={2} name={t('evolution.waistLine')} />
                </LineChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}
    </>
  );
}
